/*
 * ew-livery.js — Livery reskin system (A3b: three-layer architecture)
 *
 * The visitor "wraps" the monochrome base site in a colored livery
 * (a decorative theme pack). Liveries are data-driven: defined as
 * `livery` metaobjects, serialized into the JSON island by
 * snippets/livery-overlay.liquid. Adding a livery = a new metaobject,
 * never code.
 *
 * RENDERING (A3b): there is no on-top overlay. The livery is the
 * BACKGROUND of the page — base + livery + content are three locked
 * layers (see ew-livery.css / layout/theme.liquid). This file only
 * manages STATE + the placeholder watermark:
 *   - sets body[data-livery="<id>"] (the one global switch)
 *   - sets --livery-accent (+ --livery-motif when real art exists)
 *   - tiles the placeholder block-text watermark into .ew-layer-livery
 *
 * State:
 *   Anonymous       -> localStorage['ew_livery']
 *   Active customer -> also reads window.ewLiveryContext.customerLivery
 *
 * Eligibility (date range / customer tag) is evaluated client-side
 * from window.ewLiveryContext; the three seeded liveries set none.
 *
 * Events on document: ew:livery-changed { detail:{ livery, prev } }
 *
 * Public API: window.EWLivery
 *   .all()                     -> array of livery definitions (eligible only)
 *   .get(id)                   -> one definition (or null)
 *   .read()                    -> active livery id | null (neutral)
 *   .set(id)                   -> persist + apply + event
 *   .clear()                   -> revert to neutral
 *   .rulesFor(id)              -> merchandising rules object for a livery
 *
 * Livery-v1 (the marketing-layer switch) extensions:
 *   .readUseCase()             -> active use case key | null
 *   .setUseCase(useCase)       -> persist use case, derive + set livery,
 *                                 broadcast ew:use-case-changed
 *   .preGuessUseCase(chassis, engine) -> recommended use case for a
 *                                 chassis+engine combo (the pre-guess
 *                                 highlighted card on the capture row)
 *   .label(key)                -> public display label for a stored
 *                                 use-case/livery token ("oemplus" → "OEM+");
 *                                 unknown keys pass through unchanged
 *   .USE_CASE_TO_LIVERY        -> the spec's mapping object (immutable)
 */
(function () {
  'use strict';

  var LS_KEY     = 'ew_livery';
  var LS_USECASE = 'ew_use_case';
  /* Sidecar timestamp covering BOTH ew_livery and ew_use_case (they are
     written/cleared together, so one ts gates both). Value shape of
     LS_KEY stays a bare livery-id string — back-compatible. */
  var LS_TS_KEY  = 'ew_livery_ts';
  /* GAR-1.6 anonymous persistence TTL. Mirror constant in
     ew-vehicle-resolver.js (separate IIFE — keep both in sync). ~365
     days, sliding window: refreshed on every write(), expired only when
     untouched for a year. */
  var TTL_MS     = 365 * 24 * 60 * 60 * 1000;

  /* ============================================================
     LIVERY_COLORS — centralized color mapping (capture-v3 #1).

     One source of truth for the pill's ambient livery color. The
     metaobject accent_color is still authoritative (LP edits this
     in admin); this map is the static fallback when the metaobject's
     accent is blank or a livery is set programmatically. To swap to
     per-use-case colors (6 instead of 3) later: keep this map keyed
     on livery and add a per-use-case override layer in setUseCase.
     ============================================================ */
  var LIVERY_COLORS = Object.freeze({
    performance: '#c2ff00',   /* telemetry green                 */
    drifter:     '#ff6a3d',   /* signal orange                   */
    oemplus:     '#5fd2ff'    /* OEM+ accent — RGB 95, 210, 255      */
  });

  /* ============================================================
     Livery-v1 — use-case capture model
     (livery-marketing-layer-v1-spec.md §"Capture model")
     ============================================================ */

  /* Use case → livery mapping (spec §"Use-case set → livery").

     3-tier collapse (LP 2026-05-29): the public selectors now ask
     "What kind of build is it?" with three 1:1 answers — Performance /
     OEM+ / Drift. Those three canonical keys live at the top. The
     original six driving-style keys are RETAINED below for back-compat:
       - PREGUESS_MAP still emits them (street/track/daily/…),
       - visitors may have one persisted in localStorage from the old
         six-card capture row.
     Both layers resolve to the same three liveries, so nothing breaks. */
  var USE_CASE_TO_LIVERY = Object.freeze({
    /* canonical 3-tier (1:1 with liveries) */
    performance: 'performance', /* Performance build → Performance    */
    oemplus:     'oemplus',     /* OEM+ build        → OEM+           */
    drift:       'drifter',     /* Drift build       → Drift          */
    /* legacy driving-style keys (back-compat; pre-guess + stored) */
    daily:     'oemplus',      /* Daily driver        → OEM+         */
    street:    'performance',  /* Street / spirited   → Performance  */
    track:     'performance',  /* Track / HPDE        → Performance  */
    roll_race: 'performance',  /* Roll race / pulls   → Performance  */
    show:      'oemplus'       /* Show / cruise       → OEM+         */
  });

  /* ============================================================
     GATE1-1 F-12 — token → PUBLIC label map (display-time ONLY).
     The single formatter for every surface that renders a stored
     use-case or livery token as visible text (header pill badge,
     etc.). Storage keys/tokens are UNCHANGED — never feed label()
     output back into setUseCase()/set().

     Coverage = everything readUseCase() can actually return:
     setUseCase() is the only writer of ew_use_case and it rejects
     keys outside USE_CASE_TO_LIVERY, so the stored value is one of
     the three canonical 3-tier keys (performance/oemplus/drift) or
     a legacy driving-style key persisted by the old six-card row
     (daily/street/track/roll_race/show). The livery ids
     (performance/oemplus/drifter) are covered too so livery tokens
     format through the same map. Unknown key → returned unchanged.
     ============================================================ */
  var PUBLIC_LABELS = Object.freeze({
    /* canonical 3-tier use-case keys (+ livery ids performance/oemplus) */
    performance: 'Performance',
    oemplus:     'OEM+',
    drift:       'Drift',
    /* livery id for the Drift build */
    drifter:     'Drift',
    /* legacy driving-style keys (back-compat; may persist in localStorage) */
    daily:     'Daily',
    street:    'Street',
    track:     'Track',
    roll_race: 'Roll race',
    show:      'Show'
  });

  function label(key) {
    if (!key) return key;
    return PUBLIC_LABELS[String(key).toLowerCase().trim()] || key;
  }

  /* Chassis + engine → pre-guess use case (spec §"Chassis-family →
     default use-case pre-guess map"). The key is "ENGINE:CHASSIS"
     (engine family + chassis code). DRIFT IS NEVER AUTO-SET — it
     is shown as a one-tap option on RWD/manual chassis but never
     pre-highlighted. Fallback if no entry: "street" (Performance —
     the safest default for an enthusiast site). */
  var PREGUESS_MAP = {
    /* S55 M-cars → Track (Performance) */
    'S55:F80': 'track', 'S55:F82': 'track', 'S55:F83': 'track', 'S55:F87': 'track',
    /* S58 M-cars → Track (Performance) */
    'S58:G80': 'track', 'S58:G81': 'track', 'S58:G82': 'track', 'S58:G83': 'track',
    'S58:G87': 'track', 'S58:F97': 'track', 'S58:F98': 'track',
    /* N55 sport sixes → Street (Performance) */
    'N55:F20': 'street', 'N55:F21': 'street', 'N55:F22': 'street', 'N55:F23': 'street',
    'N55:F30': 'street', 'N55:F31': 'street', 'N55:F32': 'street', 'N55:F33': 'street',
    'N55:F34': 'street', 'N55:F36': 'street', 'N55:F87': 'street',
    'N55:F10': 'street', 'N55:F11': 'street', 'N55:F12': 'street', 'N55:F13': 'street',
    'N55:F06': 'street', 'N55:F15': 'street', 'N55:F16': 'street', 'N55:F25': 'street', 'N55:F26': 'street',
    /* B58 sport (sedan/coupe sport sixes) → Street (Performance) */
    'B58:F22': 'street', 'B58:F23': 'street', 'B58:F30': 'street', 'B58:F31': 'street',
    'B58:F32': 'street', 'B58:F33': 'street', 'B58:F36': 'street',
    'B58:G20': 'street', 'B58:G21': 'street', 'B58:G22': 'street', 'B58:G23': 'street',
    'B58:G26': 'street', 'B58:G29': 'street', 'B58:G42': 'street',
    'B58:G01': 'street', 'B58:G02': 'street', 'B58:G30': 'street', 'B58:G31': 'street',
    /* B58 big-body / luxury GT → Daily (OEM+) */
    'B58:G11': 'daily', 'B58:G12': 'daily', 'B58:G14': 'daily', 'B58:G15': 'daily', 'B58:G16': 'daily',
    'B58:G05': 'daily', 'B58:G06': 'daily', 'B58:G07': 'daily',
    /* S63 halo (big-body M) → Street (Performance) */
    'S63:F10': 'street', 'S63:F06': 'street', 'S63:F12': 'street', 'S63:F13': 'street',
    'S63:F85': 'street', 'S63:F86': 'street', 'S63:F90': 'street',
    'S63:F95': 'street', 'S63:F96': 'street',
    /* N63 big-body luxury → Daily (OEM+) */
    'N63:F01': 'daily', 'N63:F02': 'daily', 'N63:F06': 'daily', 'N63:F10': 'daily',
    'N63:F11': 'daily', 'N63:F12': 'daily', 'N63:F13': 'daily',
    'N63:F15': 'daily', 'N63:F16': 'daily',
    'N63:G05': 'daily', 'N63:G06': 'daily', 'N63:G07': 'daily',
    'N63:G11': 'daily', 'N63:G12': 'daily', 'N63:G14': 'daily', 'N63:G15': 'daily', 'N63:G16': 'daily',
    'N63:G30': 'daily',
    /* N20 / N26 sport coupe/sedan → Street (Performance) */
    'N20:F22': 'street', 'N20:F23': 'street', 'N20:F30': 'street', 'N20:F31': 'street',
    'N20:F32': 'street', 'N20:F33': 'street', 'N20:F36': 'street',
    'N20:F10': 'street', 'N20:F11': 'street', 'N20:F25': 'street', 'N20:F26': 'street',
    /* B48 BMW sport → Street (Performance) */
    'B48:F22': 'street', 'B48:F23': 'street', 'B48:F30': 'street', 'B48:F31': 'street',
    'B48:F32': 'street', 'B48:F33': 'street', 'B48:F36': 'street', 'B48:F39': 'street', 'B48:F48': 'street',
    'B48:G20': 'street', 'B48:G21': 'street', 'B48:G22': 'street', 'B48:G23': 'street',
    'B48:G26': 'street', 'B48:G29': 'street', 'B48:G42': 'street',
    'B48:G01': 'street', 'B48:G02': 'street',
    /* B48 MINI Cooper S / Karen profile → Daily (OEM+). JCW model
       on the same chassis still falls into Daily by default — the
       user is one tap from Street/Track if they want Performance. */
    'B48:F54': 'daily', 'B48:F55': 'daily', 'B48:F56': 'daily', 'B48:F57': 'daily', 'B48:F60': 'daily'
  };

  /* RWD / manual-capable chassis where the Drift tap-card should be
     visually prominent (NEVER pre-highlighted — only emphasized as a
     one-tap option). The capture-section JS reads this list to set a
     data-drift-prominent flag per chassis. */
  var DRIFT_PROMINENT_CHASSIS = Object.freeze([
    /* RWD sport sixes */
    'F22','F23','F87',                    /* 2-series RWD, M2 */
    'F30','F31','F32','F33','F36',        /* 3/4 series RWD */
    'G20','G21','G22','G23','G26','G42',  /* G-chassis 3/4/2-series RWD */
    'G29',                                /* Z4 */
    'A90','J29',                          /* Supra */
    'F80','F82','F83',                    /* F-series M3/M4 */
    'G80','G81','G82','G83','G87'         /* G-series M3/M4/M2 */
  ]);

  function preGuessUseCase(chassis, engine) {
    if (!chassis) return 'street';
    var key = (engine || '') + ':' + chassis;
    return PREGUESS_MAP[key] || 'street'; /* spec default */
  }

  /* TTL gate for the anonymous livery + use-case localStorage values.
     Shared by read()/readUseCase(). Only touches the local (anonymous)
     keys — the server customerLivery fallback is never expired here.
     - ts present + stale: wipe BOTH local keys + ts, return true (expired).
     - ts present + fresh: return false.
     - ts MISSING but a value exists (legacy / pre-GAR-1.6): stamp now,
       return false (back-compat, no surprise wipe). */
  function liveryExpired() {
    try {
      var hasVal = localStorage.getItem(LS_KEY) != null ||
                   localStorage.getItem(LS_USECASE) != null;
      if (!hasVal) return false;
      var tsRaw = localStorage.getItem(LS_TS_KEY);
      if (tsRaw == null) {
        localStorage.setItem(LS_TS_KEY, String(Date.now()));
        return false;
      }
      var ts = parseInt(tsRaw, 10);
      if (isFinite(ts) && (Date.now() - ts) > TTL_MS) {
        localStorage.removeItem(LS_KEY);
        localStorage.removeItem(LS_USECASE);
        localStorage.removeItem(LS_TS_KEY);
        return true;
      }
    } catch (_) {}
    return false;
  }

  function stampLiveryTs() {
    try { localStorage.setItem(LS_TS_KEY, String(Date.now())); } catch (_) {}
  }

  function readUseCase() {
    liveryExpired();
    try { return localStorage.getItem(LS_USECASE) || null; } catch (_) { return null; }
  }
  function writeUseCase(uc) {
    try {
      if (uc) { localStorage.setItem(LS_USECASE, uc); stampLiveryTs(); }
      else localStorage.removeItem(LS_USECASE);
    } catch (_) {}
  }

  /* Set the use case, derive the livery, and fire both events.
     Use cases the spec does not recognize fall through silently. */
  function setUseCase(uc) {
    if (!uc || !USE_CASE_TO_LIVERY[uc]) return;
    var prev = readUseCase();
    writeUseCase(uc);
    /* Derive livery; if customer is already wearing the right one,
       set() is a no-op (it short-circuits when prev === id). */
    var liveryId = USE_CASE_TO_LIVERY[uc];
    document.dispatchEvent(new CustomEvent('ew:use-case-changed', {
      detail: { useCase: uc, prevUseCase: prev, livery: liveryId }
    }));
    /* This also triggers ew:livery-changed via set(). The function
       is hoisted into scope below; this call resolves at runtime. */
    set(liveryId);
  }

  function ctx() { return window.ewLiveryContext || { today: '', customerTags: [], customerLivery: null }; }

  function loadDefs() {
    var el = document.querySelector('[data-ew-livery-data]');
    if (!el) return [];
    var raw;
    try { raw = JSON.parse(el.textContent || '[]'); } catch (_) { return []; }
    return raw.map(function (d) {
      var rules = d.rules;
      if (typeof rules === 'string') { try { rules = JSON.parse(rules); } catch (_) { rules = {}; } }
      /* Accent precedence: metaobject accent_color (LP authoritative)
         → LIVERY_COLORS fallback (centralized in this file)
         → final hard fallback (house red, matches CSS resting --accent). */
      return {
        id: d.id, label: d.label, descriptor: d.descriptor,
        accent: d.accent || LIVERY_COLORS[d.id] || '#E11D2C',
        motif: d.motif || null,
        blend: d.blend || null,
        rules: rules || { types: [], tags: [], vendors: [] },
        sort: parseInt(d.sort, 10) || 0,
        eligStart: d.elig_start || null,
        eligEnd: d.elig_end || null,
        eligTag: d.elig_tag || null,
      };
    }).sort(function (a, b) { return a.sort - b.sort; });
  }

  var DEFS = loadDefs();

  function isEligible(def) {
    var c = ctx();
    if (def.eligStart && c.today && c.today < def.eligStart) return false;
    if (def.eligEnd && c.today && c.today > def.eligEnd) return false;
    if (def.eligTag) {
      var tags = (c.customerTags || []).map(function (t) { return String(t).toLowerCase(); });
      if (tags.indexOf(String(def.eligTag).toLowerCase()) === -1) return false;
    }
    return true;
  }

  function all() { return DEFS.filter(isEligible); }
  function get(id) {
    for (var i = 0; i < DEFS.length; i++) if (DEFS[i].id === id) return DEFS[i];
    return null;
  }
  function rulesFor(id) { var d = get(id); return d ? d.rules : null; }

  function read() {
    /* Expire the anonymous local livery first (sidecar TTL); the server
       customerLivery fallback below is never time-gated. */
    liveryExpired();
    var stored = null;
    try { stored = localStorage.getItem(LS_KEY); } catch (_) {}
    if (!stored && ctx().customerLivery) stored = ctx().customerLivery;
    if (!stored) return null;
    var def = get(stored);
    /* Stored livery no longer eligible (date passed / tag removed) ->
       fall back to neutral so an expired pack doesn't stick. */
    if (!def || !isEligible(def)) return null;
    return stored;
  }

  function write(id) {
    try {
      if (id) { localStorage.setItem(LS_KEY, id); stampLiveryTs(); }
      else localStorage.removeItem(LS_KEY);
    } catch (_) {}
  }

  /* ---- Reflect the active livery onto every selection trigger ----
     Every [data-ew-livery-set] element (the "Who are you?" hero tiles,
     the footer switcher items) gets aria-pressed="true" when its livery
     is the active one, "false" otherwise; [data-ew-livery-clear] gets
     aria-pressed when neutral. aria-pressed is correct toggle semantics
     AND the CSS hook for the persistent "lit" state. Because apply()
     runs on every page load (from the persisted livery), the active
     tile is always lit on arrival — it behaves like a latched switch. */
  function syncTriggers(activeId) {
    var sets = document.querySelectorAll('[data-ew-livery-set]');
    for (var i = 0; i < sets.length; i++) {
      sets[i].setAttribute('aria-pressed',
        sets[i].getAttribute('data-ew-livery-set') === activeId ? 'true' : 'false');
    }
    var clears = document.querySelectorAll('[data-ew-livery-clear]');
    for (var j = 0; j < clears.length; j++) {
      clears[j].setAttribute('aria-pressed', activeId ? 'false' : 'true');
    }
  }

  /* ---- Apply: the one global switch + livery custom properties ---- */
  function apply(id) {
    var def = id ? get(id) : null;
    var active = (def && isEligible(def)) ? def : null;
    var body = document.body;
    if (active) {
      body.setAttribute('data-livery', active.id);
      body.style.setProperty('--livery-accent', active.accent);
    } else {
      body.removeAttribute('data-livery');
      body.style.removeProperty('--livery-accent');
    }
    /* bug #21: the watermark + motif backdrop was removed. Livery state
       still drives copy voice and CTA accent; the visual ambient art
       slot is held in DOM but renders nothing. */
    syncTriggers(active ? active.id : null);
  }

  /* bug #21: watermark tiler removed. The slot ([data-ew-livery-motif])
     stays in the DOM but renders nothing — see ew-livery.css. reflow()
     kept as a no-op so any external caller (resize handlers, late-
     content listeners) doesn't error out. */
  function reflow() { /* no-op (bug #21) */ }

  function set(id) {
    var prev = read();
    var def = get(id);
    if (!def || !isEligible(def)) { clear(); return; }
    if (prev === id) return;
    write(id);
    apply(id);
    document.dispatchEvent(new CustomEvent('ew:livery-changed', { detail: { livery: id, prev: prev } }));
  }

  function clear() {
    var prev = read();
    write(null);
    /* Clean reset: drop the use-case + TTL sidecar alongside the livery
       so a neutral state leaves no orphaned anonymous keys. */
    try {
      localStorage.removeItem(LS_USECASE);
      localStorage.removeItem(LS_TS_KEY);
    } catch (_) {}
    apply(null);
    document.dispatchEvent(new CustomEvent('ew:livery-changed', { detail: { livery: null, prev: prev } }));
  }

  /* ---- Global selection delegation ----
     One handler covers every [data-ew-livery-set] / [data-ew-livery-clear]
     trigger sitewide: the "Who are you?" hero tiles AND the footer
     switcher. (The footer switcher also runs its own menu-close logic;
     set()/clear() are idempotent so the duplicate call is a no-op.) */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var setEl = e.target.closest('[data-ew-livery-set]');
    if (setEl) { set(setEl.getAttribute('data-ew-livery-set')); return; }
    var clearEl = e.target.closest('[data-ew-livery-clear]');
    if (clearEl) { clear(); }
  });

  /* ============================================================
     Entry-seeding (livery-v1 §"State, persistence, override, defaults")
     Land on /tuning-drift → Drift; /tuning-performance → Performance;
     /tuning-oem-plus → OEM+. BM3 referral → Performance (best-effort).
     Only fires when NO livery has been chosen yet — never overrides
     an explicit user choice (returning visitor / Garage / prior tap).
     ============================================================ */
  function seedFromEntry() {
    if (read()) return; /* user already declared — don't override */
    var liveryFromPath = null;
    try {
      var p = (location.pathname || '').toLowerCase();
      if (p.indexOf('/tuning-drift') !== -1)        liveryFromPath = 'drifter';
      else if (p.indexOf('/tuning-performance') !== -1) liveryFromPath = 'performance';
      else if (p.indexOf('/tuning-oem-plus') !== -1)    liveryFromPath = 'oemplus';
    } catch (_) {}
    /* BM3 referral — best-effort. Their domain set is the bootmod3
       directory + the core site. */
    if (!liveryFromPath) {
      try {
        var r = (document.referrer || '').toLowerCase();
        if (r.indexOf('bootmod3.com') !== -1 || r.indexOf('protuningfreaks.com') !== -1) {
          liveryFromPath = 'performance';
        }
      } catch (_) {}
    }
    if (liveryFromPath) {
      /* Seeding sets the livery but NOT a use case — the visitor
         hasn't said how they drive yet, so the capture row still
         shows when they get to a page that hosts it. */
      var def = get(liveryFromPath);
      if (def && isEligible(def)) {
        write(liveryFromPath);
        apply(liveryFromPath);
        document.dispatchEvent(new CustomEvent('ew:livery-changed', {
          detail: { livery: liveryFromPath, prev: null, source: 'entry-seed' }
        }));
      }
    }
  }

  function boot() {
    seedFromEntry();
    apply(read());
    window.addEventListener('resize', reflow);
    window.addEventListener('load', reflow);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.EWLivery = {
    all: all, get: get, read: read, set: set, clear: clear, rulesFor: rulesFor,
    /* livery-v1 (use-case capture) */
    readUseCase:     readUseCase,
    setUseCase:      setUseCase,
    preGuessUseCase: preGuessUseCase,
    /* GATE1-1 F-12 — token → public display label (use-case + livery) */
    label:           label,
    USE_CASE_TO_LIVERY: USE_CASE_TO_LIVERY,
    DRIFT_PROMINENT_CHASSIS: DRIFT_PROMINENT_CHASSIS,
    /* capture-v3 — palette mapping (single source of truth for the
       combined header pill's ambient livery color) */
    LIVERY_COLORS:   LIVERY_COLORS
  };
})();
